/**
 * <quick-size-sheet> — add to bag from a product card (snippets/quick-size-sheet.liquid).
 *
 * A `[data-quick-size]` button opens that product's sizes, read from `{product url}.js`.
 * With `data-quick-size-inline` (the card's bag+) they slide up over the card image
 * and tapping one adds it; otherwise they open in the bottom sheet, where Add to cart
 * adds the selected size. Both go through the cart-notification plumbing the PDP form
 * uses, so the bag badge and toast update as usual. A single-variant product just adds.
 * The inline panel's Size chart link opens a small modal with the PDP chart's table,
 * fetched from the render-only quick-size-chart section; tapping a row picks that size
 * and the modal's Add to cart adds it.
 *
 * Uses Dawn's classic-script globals (fetchConfig, routes, publish, PUB_SUB_EVENTS).
 */
const SIZE_OPTION = /size/i;
const CLOSE_MS = 300; // keep in sync with the panel transitions
const CHART_CLOSE_MS = 200;
const CHART_SECTION = 'quick-size-chart';

class QuickSizeSheet extends HTMLElement {
  connectedCallback() {
    this.titleElement = this.querySelector('[data-quick-size-title]');
    this.optionsElement = this.querySelector('[data-quick-size-options]');
    this.errorElement = this.querySelector('[data-quick-size-error]');
    this.panel = this.querySelector('[role="dialog"]');
    this.submitButton = this.querySelector('[data-quick-size-submit]');
    this.inlineTemplate = this.querySelector('template[data-quick-size-inline]');
    this.products = new Map();
    this.chartModal = document.querySelector('[data-quick-size-chart]');
    this.chartBody = this.chartModal?.querySelector('[data-quick-size-chart-body]');
    this.chartUnits = this.chartModal?.querySelector('[data-quick-size-chart-units]');
    this.chartFooter = this.chartModal?.querySelector('[data-quick-size-chart-footer]');
    this.chartError = this.chartModal?.querySelector('[data-quick-size-chart-error]');
    this.chartAddButton = this.chartModal?.querySelector('[data-quick-size-chart-add]');
    this.charts = new Map();

    this.documentClickHandler = (event) => {
      if (this.chartModal?.contains(event.target)) {
        this.handleChartClick(event);
        return;
      }
      const trigger = event.target.closest('[data-quick-size]');
      if (trigger) {
        event.preventDefault();
        if (trigger.hasAttribute('data-quick-size-inline')) this.openInline(trigger);
        else this.open(trigger);
        return;
      }
      if (this.inlinePanel) this.handleInlineClick(event);
    };
    document.addEventListener('click', this.documentClickHandler);

    this.addEventListener('click', async (event) => {
      if (event.target.closest('[data-quick-size-close]')) {
        this.close();
        return;
      }
      const size = event.target.closest('[data-variant-id]');
      if (size && !size.disabled) {
        this.select(size);
        return;
      }
      if (event.target.closest('[data-quick-size-submit]')) {
        const selected = this.optionsElement.querySelector('[aria-pressed="true"]');
        if (!selected) {
          this.showError(this.dataset.selectText);
          return;
        }
        this.errorElement.hidden = true;
        const result = await this.add(selected.dataset.variantId, this.submitButton);
        if (result?.added) this.close();
        else if (result) this.showError(result.message);
      }
    });

    this.keyHandler = (event) => {
      if (event.key !== 'Escape') return;
      if (this.chartModal && !this.chartModal.hidden) {
        this.closeChart();
        return;
      }
      if (this.inlinePanel) this.closeInline();
      if (this.classList.contains('is-open')) this.close();
    };
    document.addEventListener('keydown', this.keyHandler);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.documentClickHandler);
    document.removeEventListener('keydown', this.keyHandler);
  }

  async open(trigger) {
    this.trigger = trigger;
    this.errorElement.hidden = true;
    trigger.classList.add('is-loading');
    try {
      const product = await this.load(trigger.dataset.quickSize);
      if (product.variants.length === 1) {
        const result = await this.add(String(product.variants[0].id));
        if (result && !result.added) {
          this.showError(result.message);
          this.show();
        }
        return;
      }
      this.titleElement.textContent = product.title;
      this.optionsElement.replaceChildren(...this.sizeButtons(product));
      this.show();
    } catch {
      this.titleElement.textContent = trigger.dataset.productTitle || '';
      this.optionsElement.replaceChildren();
      this.showError();
      this.show();
    } finally {
      trigger.classList.remove('is-loading');
    }
  }

  async openInline(trigger) {
    const host = trigger.closest('[data-quick-size-host]');
    this.closeInline();
    this.trigger = trigger;
    trigger.classList.add('is-loading');
    let product;
    try {
      product = await this.load(trigger.dataset.quickSize);
    } catch {
      this.showInline(host, [], this.dataset.errorText);
      return;
    } finally {
      trigger.classList.remove('is-loading');
    }

    if (product.variants.length === 1) {
      const result = await this.add(String(product.variants[0].id), trigger);
      if (result && !result.added) this.showInline(host, [], result.message);
      return;
    }
    this.showInline(host, this.sizeButtons(product));
  }

  showInline(host, buttons, message) {
    const panel = this.inlineTemplate.content.firstElementChild.cloneNode(true);
    panel.querySelector('[data-quick-size-options]').replaceChildren(...buttons);
    panel.querySelector('[data-quick-size-chart-open]').hidden = !(
      buttons.length && this.trigger.hasAttribute('data-size-chart-available')
    );
    if (message) this.setInlineError(panel, message);
    host.append(panel);
    host.classList.add('has-quick-size');
    this.inlinePanel = panel;

    panel.offsetHeight; // commit the closed state so the slide-up transitions
    panel.classList.add('is-open');
    (panel.querySelector('.quick-size__size:not(:disabled)') || panel).focus({ preventScroll: true });
  }

  closeInline() {
    const panel = this.inlinePanel;
    if (!panel) return;
    this.inlinePanel = null;
    const hadFocus = panel.contains(document.activeElement);
    panel.classList.remove('is-open');
    panel.parentElement.classList.remove('has-quick-size');
    setTimeout(() => panel.remove(), CLOSE_MS);
    if (hadFocus) this.trigger?.focus({ preventScroll: true });
  }

  async handleInlineClick(event) {
    const panel = this.inlinePanel;
    if (!panel.contains(event.target) || event.target.closest('[data-quick-size-close]')) {
      this.closeInline();
      return;
    }
    if (event.target.closest('[data-quick-size-chart-open]')) {
      this.openChart();
      return;
    }
    const size = event.target.closest('[data-variant-id]');
    if (!size || size.disabled) return;

    this.setInlineError(panel, '');
    const result = await this.add(size.dataset.variantId, size);
    if (!result || this.inlinePanel !== panel) return;
    if (result.added) this.closeInline();
    else this.setInlineError(panel, result.message);
  }

  setInlineError(panel, message) {
    const error = panel.querySelector('[data-quick-size-error]');
    error.textContent = message;
    error.hidden = !message;
  }

  async openChart() {
    const url = this.trigger.dataset.quickSize;
    const modal = this.chartModal;
    clearTimeout(this.chartHideTimer);
    this.chartUrl = url;
    modal.querySelector('[data-quick-size-chart-product]').textContent = this.trigger.dataset.productTitle || '';
    this.chartUnits.hidden = true;
    this.chartFooter.hidden = true;
    this.chartError.hidden = true;
    this.chartBody.replaceChildren();
    this.chartBody.setAttribute('aria-busy', 'true');

    modal.hidden = false;
    modal.offsetHeight; // commit the closed state so the open transitions
    modal.classList.add('is-open');
    document.documentElement.classList.add('quick-size-open');
    modal.querySelector('[role="dialog"]').focus({ preventScroll: true });

    try {
      const chart = await this.loadChart(url);
      if (this.chartUrl !== url) return;
      this.chartBody.replaceChildren(chart.cloneNode(true));
      this.chartUnits.hidden = !chart.querySelector('[data-in]');
      this.setChartUnit('in');
      this.linkChartRows();
    } catch {
      if (this.chartUrl === url) this.chartBody.textContent = modal.dataset.errorText;
    } finally {
      if (this.chartUrl === url) this.chartBody.removeAttribute('aria-busy');
    }
  }

  closeChart() {
    const modal = this.chartModal;
    if (modal.hidden) return;
    this.chartUrl = null;
    modal.classList.remove('is-open');
    if (!this.classList.contains('is-open')) document.documentElement.classList.remove('quick-size-open');
    this.chartHideTimer = setTimeout(() => (modal.hidden = true), CHART_CLOSE_MS);
    this.inlinePanel?.querySelector('[data-quick-size-chart-open]')?.focus({ preventScroll: true });
  }

  // Ties each chart row to the panel's size button for that size: rows without an
  // in-stock variant can't be picked. The footer only shows when a row can be.
  linkChartRows() {
    const sizes = new Map(
      Array.from(this.inlinePanel?.querySelectorAll('[data-variant-id]') ?? [], (button) => [button.textContent.trim(), button])
    );
    let selectable = false;
    this.chartBody.querySelectorAll('[data-szc-row]').forEach((row) => {
      const input = row.querySelector('[data-size-value]');
      const size = sizes.get(input?.dataset.sizeValue);
      const available = Boolean(size && !size.disabled);
      row.classList.toggle('is-unavailable', !available);
      input.disabled = !available;
      if (available) {
        row.dataset.variantId = size.dataset.variantId;
        selectable = true;
        if (size.getAttribute('aria-pressed') === 'true') this.selectChartRow(row);
      }
    });
    this.chartFooter.hidden = !selectable;
  }

  selectChartRow(row) {
    this.chartBody.querySelectorAll('[data-szc-row]').forEach((other) => {
      const selected = other === row;
      other.classList.toggle('is-selected', selected);
      const input = other.querySelector('[data-size-value]');
      if (input) input.checked = selected;
    });
    this.chartError.hidden = true;
    const size = this.inlinePanel?.querySelector(`[data-variant-id="${row.dataset.variantId}"]`);
    if (size) this.select(size, this.inlinePanel);
  }

  async handleChartClick(event) {
    if (event.target.closest('[data-quick-size-chart-close]')) {
      this.closeChart();
      return;
    }
    const unit = event.target.closest('[data-unit]');
    if (unit) {
      this.setChartUnit(unit.dataset.unit);
      return;
    }
    const row = event.target.closest('[data-szc-row]');
    if (row) {
      if (row.dataset.variantId) this.selectChartRow(row);
      return;
    }
    if (!event.target.closest('[data-quick-size-chart-add]')) return;

    const selected = this.chartBody.querySelector('[data-szc-row].is-selected');
    if (!selected) {
      this.showChartError(this.dataset.selectText);
      return;
    }
    this.chartError.hidden = true;
    const result = await this.add(selected.dataset.variantId, this.chartAddButton);
    if (!result) return;
    if (result.added) {
      this.closeChart();
      this.closeInline();
    } else {
      this.showChartError(result.message);
    }
  }

  showChartError(message) {
    this.chartError.textContent = message;
    this.chartError.hidden = false;
  }

  setChartUnit(unit) {
    this.chartUnits
      .querySelectorAll('[data-unit]')
      .forEach((button) => button.setAttribute('aria-pressed', String(button.dataset.unit === unit)));
    this.chartBody.querySelectorAll('[data-in]').forEach((cell) => (cell.textContent = cell.dataset[unit]));
  }

  // The PDP chart's table, or its "coming soon" note.
  async loadChart(url) {
    if (!this.charts.has(url)) {
      const response = await fetch(`${url.split('?')[0]}?section_id=${CHART_SECTION}`);
      if (!response.ok) throw new Error(`size chart ${response.status}`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const chart = html.querySelector('.szc__table-wrap') || html.querySelector('[data-szc-panel="chart"] .szc__ph');
      if (!chart) throw new Error('no size chart');
      this.charts.set(url, chart);
    }
    return this.charts.get(url);
  }

  async load(url) {
    if (!this.products.has(url)) {
      const response = await fetch(`${url.split('?')[0]}.js`);
      if (!response.ok) throw new Error(`product ${response.status}`);
      this.products.set(url, await response.json());
    }
    return this.products.get(url);
  }

  // One button per size, in the product's own order; each takes the first variant
  // in stock with that size (other options, e.g. colour, follow the card's product).
  sizeButtons(product) {
    const optionNames = product.options.map((option) => option.name ?? option);
    let sizeIndex = optionNames.findIndex((name) => SIZE_OPTION.test(name));
    if (sizeIndex === -1) sizeIndex = optionNames.length - 1;

    const bySize = new Map();
    product.variants.forEach((variant) => {
      const size = variant.options[sizeIndex];
      const current = bySize.get(size);
      if (!current || (!current.available && variant.available)) bySize.set(size, variant);
    });

    return Array.from(bySize, ([size, variant]) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'quick-size__size';
      button.setAttribute('role', 'listitem');
      button.dataset.variantId = variant.id;
      button.disabled = !variant.available;
      button.setAttribute('aria-pressed', 'false');
      button.textContent = size;
      return button;
    });
  }

  select(size, container = this) {
    container.querySelectorAll('[aria-pressed="true"]').forEach((button) => button.setAttribute('aria-pressed', 'false'));
    size.setAttribute('aria-pressed', 'true');
    this.errorElement.hidden = true;
  }

  // Resolves to { added: true }, { added: false, message }, or undefined while another add runs.
  async add(variantId, button) {
    if (this.adding) return undefined;
    this.adding = true;
    button?.classList.add('is-adding');
    const inlinePanel = button?.closest('.quick-size-inline');
    const status = inlinePanel?.querySelector('[data-quick-size-status]');
    inlinePanel?.classList.add('is-adding');
    if (status) status.hidden = false;

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    if (cart) {
      formData.append(
        'sections',
        cart.getSectionsToRender().map((section) => section.id)
      );
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(this.trigger);
    }
    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    try {
      const response = await (await fetch(routes.cart_add_url, config)).json();
      if (response.status) return { added: false, message: response.description || response.message || this.dataset.errorText };
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-size', productVariantId: variantId, cartData: response });
      if (cart) cart.renderContents(response);
      else window.location = routes.cart_url;
      return { added: true };
    } catch {
      return { added: false, message: this.dataset.errorText };
    } finally {
      this.adding = false;
      button?.classList.remove('is-adding');
      inlinePanel?.classList.remove('is-adding');
      if (status) status.hidden = true;
    }
  }

  showError(message) {
    this.errorElement.textContent = message || this.dataset.errorText;
    this.errorElement.hidden = false;
  }

  show() {
    clearTimeout(this.hideTimer);
    this.hidden = false;
    document.documentElement.classList.add('quick-size-open');
    requestAnimationFrame(() => {
      this.classList.add('is-open');
      (this.optionsElement.querySelector('button:not(:disabled)') || this.panel).focus({ preventScroll: true });
    });
  }

  close() {
    if (!this.classList.contains('is-open')) return;
    this.classList.remove('is-open');
    document.documentElement.classList.remove('quick-size-open');
    this.hideTimer = setTimeout(() => (this.hidden = true), CLOSE_MS);
    this.trigger?.focus({ preventScroll: true });
  }
}

if (!customElements.get('quick-size-sheet')) {
  customElements.define('quick-size-sheet', QuickSizeSheet);
}
